/**
 * Module 77 (D4) — THE unlock rule: the puzzle ordinal at which power-up `id`
 * unlocks on `track`, or `null` for never.
 *
 * `unlocks.powerups` is the table every track shares (WP-65.F1); a track with
 * `powerupUnlocks: 'all'` (Expert) has every configured power-up from puzzle 1.
 * A power-up the table omits stays `null` there too — `'all'` never invents
 * one. The controller, the bar's pill, the try-it glow, the unlock popups, the
 * lessons, analytics and the layout-probe library all read this (directly or
 * through `powerupRegistry`); nothing else recomputes it.
 *
 * `track` omitted (the Studio, fixtures, a single ladder) = the shared table.
 */
export function unlockPuzzleFor(config, track, id) {
    // Module 92 — a retired power-up never unlocks, on any track.
    if (isPowerupRetired(config, id))
        return null;
    const base = config.unlocks.powerups[id];
    if (base === undefined)
        return null;
    const all = track && config.tracks.find((t) => t.id === track)?.powerupUnlocks === 'all';
    return all ? 1 : base;
}
/**
 * Module 92 — is this power-up switched off (`retired: true` in config)? The
 * one question every reader asks: the registry (and through it the bar, the
 * controller, prices and analytics), `unlockPuzzleFor`, the unlock popups and
 * the lessons. Kept in config rather than deleted (user decision), so turning
 * it back on is the flag (plus restoring the server price row 0019 drops).
 */
export function isPowerupRetired(config, id) {
    return config.powerups.some((p) => p.id === id && p.retired === true);
}
/** Every LIVE power-up the config defines, in config order, with `track`'s unlocks. */
export function powerupRegistry(config, track) {
    return config.powerups
        .filter((p) => p.retired !== true)
        .map((p) => ({
        id: p.id,
        cost: { ...p.cost },
        perLevelCap: p.perLevelCap,
        unlockPuzzle: unlockPuzzleFor(config, track, p.id),
        freeUses: p.freeUses ?? 0,
    }));
}
/** One definition by id, or `null` for an id the config does not define. */
export function powerupDefinition(config, id, track) {
    return powerupRegistry(config, track).find((p) => p.id === id) ?? null;
}
/**
 * Config mismatches between `powerups[]` and `unlocks.powerups`, in both
 * directions. Empty for a healthy config; asserted empty for the shipped one, so
 * a Studio edit that drops an unlock level fails the gate instead of quietly
 * making a power-up unreachable.
 */
export function registryIssues(config) {
    const issues = [];
    const defined = new Set(config.powerups.map((p) => p.id));
    for (const p of config.powerups) {
        if (config.unlocks.powerups[p.id] === undefined) {
            issues.push({
                id: p.id,
                kind: 'missingUnlockLevel',
                detail: `"${p.id}" has a cost and cap but no entry in unlocks.powerups, so it can never be used`,
            });
        }
    }
    for (const id of Object.keys(config.unlocks.powerups)) {
        if (!defined.has(id)) {
            issues.push({
                id,
                kind: 'unknownUnlockEntry',
                detail: `unlocks.powerups names "${id}", which no entry in powerups[] defines`,
            });
        }
    }
    return issues;
}
/** Whether a power-up is available to a player on this PUZZLE of their track. */
export function isPowerupUnlocked(definition, puzzle) {
    return definition.unlockPuzzle !== null && puzzle >= definition.unlockPuzzle;
}
/**
 * The power-ups unlocking exactly at this level — the WP-35.22 popup hook, and
 * what the controller announces to analytics. Mirrors `familiesUnlockingAt` so
 * shapes and power-ups are asked the same question the same way.
 */
export function powerupsUnlockingAt(config, puzzle, track) {
    return powerupRegistry(config, track)
        .filter((p) => p.unlockPuzzle === puzzle)
        .map((p) => p.id);
}
